import React, { useState, useEffect } from 'react';
import { Box, Typography, Card, CardContent, Divider, TextField, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { OpenInNewIcon } from 'icons/materialIcons';
import { useTeam } from 'team/contexts/teamContext';

const TeamSubscriptionList = () => {
    const navigate = useNavigate();
    const { getCurrentTeam, updateTeam, loading: teamLoading } = useTeam();
    const team = getCurrentTeam();
    const [memberPrice, setMemberPrice] = useState(team?.memberPrice || 25);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (team?.memberPrice !== undefined) {
            setMemberPrice(team.memberPrice);
        }
    }, [team?.memberPrice]);

    const handlePriceChange = (e) => {
        const value = e.target.value;
        setMemberPrice(value);
    };

    const handlePriceBlur = async () => {
        const numericPrice = parseFloat(memberPrice);
        if (isNaN(numericPrice)) {
            setMemberPrice(team?.memberPrice || 25);
            return;
        }

        if (numericPrice === team?.memberPrice) return;

        setIsSaving(true);
        try {
            await updateTeam(team._id, { memberPrice: numericPrice });
        } catch (error) {
            console.error('Failed to update member price:', error);
            setMemberPrice(team?.memberPrice || 25);
        } finally {
            setIsSaving(false);
        }
    };

    const handleAddMembers = () => {
        navigate('/team/add-members');
    };

    const blueColor = '#29aef8';

    return (
        <Box sx={{ maxWidth: '800px', mt: 4 }}>
            <Card 
                sx={{ 
                    borderRadius: '16px', 
                    overflow: 'hidden', 
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    mb: 3
                }}
            >
                <CardContent sx={{ p: 0 }}>
                    <Box 
                        sx={{ 
                            p: 3, 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            background: '#fff'
                        }}
                    >
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
                                Touch Plan Overview
                            </Typography>
                        </Box>
                    </Box>
                    
                    <Divider sx={{ mx: 3 }} />

                    <Box sx={{ p: 3, mt: 1 }}>
                        <Typography variant="body2" sx={{ color: '#666', mb: 3, fontStyle: 'italic' }}>
                            Adjust your team subscription settings below.
                        </Typography>

                        {/* Stats Section with Divider */}
                        <Box 
                            sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                backgroundColor: '#f5f5f5', 
                                borderRadius: '16px',
                                p: 3 // Increased padding for better internal spacing
                            }}
                        >
                            {/* Members Column */}
                            <Box sx={{ flex: 1, textAlign: 'center' }}>
                                <Typography sx={{ fontWeight: 500, color: '#000', fontSize: '1.5rem', lineHeight: 1.2 }}>
                                    {team?.maxTeamMembers || 10}
                                </Typography>
                                <Typography sx={{ color: '#666', fontWeight: 400, fontSize: '0.85rem', mt: 0.5, mb: 1 }}>
                                    Members Limit
                                </Typography>
                                
                                <Box 
                                    onClick={handleAddMembers}
                                    sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        color: blueColor,
                                        '&:hover': {
                                            opacity: 0.8
                                        }
                                    }}
                                >
                                    <Typography sx={{ fontWeight: 400, mr: 0.5, fontSize: '0.85rem' }}>
                                        Add More
                                    </Typography>
                                    <OpenInNewIcon sx={{ fontSize: '1rem' }} />
                                </Box>
                            </Box>

                            {/* Vertical Line */}
                            <Divider 
                                orientation="vertical" 
                                flexItem 
                                sx={{ 
                                    borderColor: '#dcdcdc', 
                                    mx: 3, 
                                    my: 2,
                                    borderWidth: '0.5px'
                                }} 
                            />

                            {/* Cost Column */}
                            <Box sx={{ flex: 1, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <Typography sx={{ color: '#666', fontWeight: 400, fontSize: '0.85rem', mb: 1 }}>
                                    Cost per Member
                                </Typography>
                                
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <TextField
                                        value={memberPrice}
                                        onChange={handlePriceChange}
                                        onBlur={handlePriceBlur}
                                        variant="outlined"
                                        size="small"
                                        type="number"
                                        sx={{
                                            width: '90px',
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px',
                                                backgroundColor: '#fff',
                                                fontSize: '1.1rem',
                                                fontWeight: 500,
                                                '& fieldset': {
                                                    borderColor: '#e0e0e0',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: blueColor,
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: blueColor,
                                                },
                                            }
                                        }}
                                        InputProps={{
                                            startAdornment: <Typography sx={{ mr: 0.2, fontWeight: 500, fontSize: '0.9rem' }}>$</Typography>
                                        }}
                                    />
                                    <Typography sx={{ ml: 1, color: '#666', fontSize: '0.8rem' }}>/ mo</Typography>
                                    {isSaving && <CircularProgress size={16} sx={{ ml: 1 }} />}
                                </Box>

                                <Divider sx={{ width: '60%', mb: 2, borderColor: '#e0e0e0' }} />

                                <Typography sx={{ fontWeight: 500, color: '#000', fontSize: '1.25rem', lineHeight: 1.2 }}>
                                    Total: ${(team?.maxTeamMembers || 10) * (parseFloat(memberPrice) || 0)}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 0.5 }}>
                                    ({team?.maxTeamMembers || 10} × ${memberPrice})
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

export default TeamSubscriptionList;

